import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullPageInfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useProtocolInsightsAccess } from '@hooks-clean/use-protocol-insights-access';
import { IFormData, ProtocolReviewContext } from '@pages-clean/protocol-insights/risk-iq/interfaces/context';
import { getProtocolInsightsPermissions } from '@utils-clean/protocol-insights-access';
import { ProtocolInsightsApp } from 'types/graphql-global-types';
import { NullableStudy } from '@contexts/local-state-context';
import { getIsActiveField } from '@utils/convertor';
import { LoggedInPageProps } from '@config/routes/interfaces';
import './index.scss';

interface IProtocolReviewHistoryProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: IProtocolReviewHistoryProps) => {
    const [formData, setFormData] = useState<IFormData>({
        study,
    });

    const StudyHasRAM = study && getIsActiveField('RAM', study.modules);

    // Same Trial.OS RiskIQ access gate as Protocol Review (ADO 90806).
    const { access, loading: accessLoading } = useProtocolInsightsAccess(ProtocolInsightsApp.RISK_IQ, study?.id);
    const { canView } = getProtocolInsightsPermissions(access);

    const protocolReviewHistoryProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
        }),
        [formData, setFormData],
    );

    const { t } = useTranslation();

    useEffect(() => {
        setFormData({
            ...formData,
            study,
        });
    }, [study?.id]);

    if (!StudyHasRAM) return <FullPageInfoField message={t('home:pageDataErrors:cannotAccessModule')} type="Info" />;

    if (accessLoading && access === undefined) return <Loading />;

    // Also reached by direct URL navigation, so the history must never render without access.
    if (!canView) return <FullPageInfoField message={t('protocolInsights:riskIQ.noAccess')} type="Info" />;

    // Placeholder - the History screen itself is built in a follow-up story.
    return (
        <div className="protocolReviewHistory-container">
            <ProtocolReviewContext.Provider value={protocolReviewHistoryProviderValue}>
                <div style={{ height: 'calc(100% - 30px)' }} data-testid="riskiq-history">
                    HISTORY
                </div>
            </ProtocolReviewContext.Provider>
        </div>
    );
};

const ProtocolReviewHistoryWrapper = ({ study }: IProtocolReviewHistoryProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const ProtocolReviewHistory = ({ study }: LoggedInPageProps) => <ProtocolReviewHistoryWrapper study={study} />;

export default ProtocolReviewHistory;
